import React, { useCallback, useContext } from "react";
import app from "../utils/firebaseApp.js";
import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { AuthContext } from "../utils/auth.js";
import { withRouter } from "react-router";
import Notification from "../components/Notification.js";
import { FcGoogle } from 'react-icons/fc';
import { isIPBlocked, getClientIP, storeRegistrationInfo } from "../utils/ipBlocker.js";
import posthog from 'posthog-js';

function Login() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorTitle, setErrorTitle] = React.useState("");
  const [errorSubtitle, setErrorSubtitle] = React.useState("");
  const [clientIP, setClientIP] = React.useState(null);

  const provider = new GoogleAuthProvider();
  const auth = getAuth(app);

  React.useEffect(() => {
    const fetchIP = async () => {
      const ip = await getClientIP();
      setClientIP(ip);
    };
    fetchIP();
  }, []);

  const signInWithGoogle = async () => {
    if (clientIP && isIPBlocked(clientIP)) {
      setError(true);
      setErrorTitle("Error");
      setErrorSubtitle("Error.");

      setTimeout(() => {
        resetError();
      }, 3000);
      return;
    }

    await signInWithPopup(auth, provider)
      .then(async (result) => {
        const user = result.user;

        await storeRegistrationInfo({
          type: 'login',
          registrationType: 'google',
          idToken: await user.getIdToken(),
          clientIP: clientIP,
        });

        posthog.identify(user.uid, {
          email: user.email,
          userId: user.uid,
          clientIp: clientIP,
        });

        posthog.capture('login', {
          type: 'google',
          email: user.email,
          clientIp: clientIP,
        });

        try {
          window.dataLayer.push({
            event: "login",
            type: "google",
          });
        } catch (error) { }

        const intendedPath = localStorage.getItem('intendedPath');
        localStorage.removeItem('intendedPath');
        window.location.href = intendedPath || "/";
      }).catch((error) => {
        const errorCode = error.code;
        alert(errorCode)
      });
  }

  const resetError = () => {
    setError(false);
    setErrorTitle("");
    setErrorSubtitle("");
  }

  const handleLogin = useCallback(
    async event => {
      event.preventDefault();
      setIsLoading(true);

      if (clientIP && isIPBlocked(clientIP)) {
        setError(true);
        setErrorTitle("Error");
        setErrorSubtitle("Error.");

        setIsLoading(false);
        setTimeout(() => {
          resetError();
        }, 3000);
        return;
      }

      const { email, password } = event.target.elements;

      try {
        const auth = getAuth(app);
        const result = await signInWithEmailAndPassword(auth, email.value, password.value);
        setIsLoading(false);
        resetError();

        await storeRegistrationInfo({
          type: 'login',
          registrationType: 'email',
          idToken: await result.user.getIdToken(),
          clientIP: clientIP,
        });

        posthog.identify(result.user.uid, {
          email: result.user.email,
          userId: result.user.uid,
          clientIp: clientIP,
        });

        posthog.capture('login', {
          type: 'email',
          email: result.user.email,
          clientIp: clientIP,
        });

        try {
          window.dataLayer.push({
            event: "login",
            type: "email",
          });
        } catch (error) { }

        const intendedPath = localStorage.getItem('intendedPath');
        localStorage.removeItem('intendedPath');
        window.location.href = intendedPath || "/";
      } catch (error) {
        setIsLoading(false);
        setError(true);
        setErrorTitle("Error");
        setErrorSubtitle("Invalid email or password");

        setTimeout(() => {
          resetError();
        }, 2000);
      }
    },
    [window.location, clientIP]
  );

  const { currentUser } = useContext(AuthContext);

  if (currentUser) {
    setTimeout(() => {
      const intendedPath = localStorage.getItem('intendedPath');
      localStorage.removeItem('intendedPath');
      window.location.href = intendedPath || "/";
    }, 2500);
  }

  return (
    <div className="h-full bg-gray-50">
      <Notification show={error} setShow={setError} title={errorTitle} subtitle={errorSubtitle} />

      <div className="flex h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-10 w-auto"
            src={process.env.PUBLIC_URL + '/logo_extracta_labs_main.png'}
            alt="Extracta LABS"
          />
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="" onSubmit={handleLogin}>
              <div className="space-y-6">
                <h2 className="text-left text-2xl font-bold leading-9 tracking-tight text-gray-900">
                  Sign in to your account
                </h2>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                    Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm leading-6">
                    <a href="/forgot" className="font-semibold text-indigo-600 hover:text-indigo-500">
                      Forgot password?
                    </a>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="mt-7 flex w-full justify-center items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-80 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </div>
              </div>
            </form>

            <div className="mb-2">
              <div className="relative mt-5">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-white px-6 text-gray-900">Or continue with</span>
                </div>
              </div>

              <button onClick={signInWithGoogle} className='mt-5 flex w-full justify-center place-items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold leading-6 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'>
                <FcGoogle size={20} className="mr-3" />
                Google
              </button>
            </div>

          </div>
          <p className="mt-10 text-center text-sm text-gray-500">
            Not a member?{' '}
            <a href="/register" className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
              Register now
            </a>
          </p>
        </div>
      </div>
    </div >
  )
}

export default withRouter(Login);